import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTabHistory } from '@/hooks/useTabHistory';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Toolbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Block as BlockIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  LockOpen as LockOpenIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Category as CategoryIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  CardGiftcard as GiftIcon,
  Insights as InsightsIcon,
  Article as ArticleIcon,
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Mail as MailIcon,
  Check as CheckIcon,
  Send as SendIcon,
  AllInclusive as AllInclusiveIcon,
} from '@mui/icons-material';
import { superAdminService, AppInterest, AppInterestStats, PendingInvitation } from '@/services/superAdminService';
import { userService } from '@/services/userService';
import { useAppSelector } from '@/hooks/redux';
import GlobalLeagueCatalogTab from '@/components/admin/GlobalLeagueCatalogTab';
import InsightsTab from '@/components/admin/InsightsTab';
import LogsTab from '@/components/admin/LogsTab';
import { IS_ADMIN_PORTAL } from '@/utils/adminDetection';
import SettingsPanel from '@/components/admin/SettingsPanel';
import { useScrollFab } from '@/hooks/useScrollFab';

const SuperAdminPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fabRef = useScrollFab(100);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'organizations';
  const initialTab = rawTab === 'waitlist' ? 'users' : rawTab;
  const { currentTab, changeTab } = useTabHistory({ initialTab });
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [unlockAccountDialogOpen, setUnlockAccountDialogOpen] = useState(false);
  const [loginHistoryDialogOpen, setLoginHistoryDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteOrgConfirmName, setDeleteOrgConfirmName] = useState('');
  const [deleteOrgError, setDeleteOrgError] = useState<string | null>(null);
  const [resetReason, setResetReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [loginHistory, setLoginHistory] = useState<any>(null);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [showOrgsSkeleton, setShowOrgsSkeleton] = useState(false);
  const [showUsersSkeleton, setShowUsersSkeleton] = useState(false);

  // App Interest state
  const [interests, setInterests] = useState<AppInterest[]>([]);
  const [interestStats, setInterestStats] = useState<AppInterestStats | null>(null);
  const [interestStatusFilter, setInterestStatusFilter] = useState<string>('');

  // Pending invitations state
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [expandedInvitationId, setExpandedInvitationId] = useState<string | null>(null);
  const [cancelInvitationDialogOpen, setCancelInvitationDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null);
  const [cancelInvitationLoading, setCancelInvitationLoading] = useState(false);

  // Waitlist invite dialog state
  const [waitlistInviteDialogOpen, setWaitlistInviteDialogOpen] = useState(false);
  const [selectedWaitlistUser, setSelectedWaitlistUser] = useState<AppInterest | null>(null);
  const [waitlistOrgName, setWaitlistOrgName] = useState('');
  const [waitlistInviteLoading, setWaitlistInviteLoading] = useState(false);
  const [waitlistInviteError, setWaitlistInviteError] = useState<string | null>(null);

  // Create Organization Dialog state
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [newOrg, setNewOrg] = useState<{ name: string; slug: string; ownerEmail: string; ownerRole: 'OWNER' | 'ADMIN' }>({ name: '', slug: '', ownerEmail: '', ownerRole: 'OWNER' });
  const [createOrgError, setCreateOrgError] = useState<string | null>(null);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);

  // Invite User Dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedOrgForInvite, setSelectedOrgForInvite] = useState<any>(null);
  const [inviteData, setInviteData] = useState({ email: '', role: 'MEMBER' as 'OWNER' | 'ADMIN' | 'MEMBER' });
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Token invalidation state
  const [invalidateTokensDialogOpen, setInvalidateTokensDialogOpen] = useState(false);
  const [invalidateAllTokensDialogOpen, setInvalidateAllTokensDialogOpen] = useState(false);
  const [invalidateTokensLoading, setInvalidateTokensLoading] = useState(false);

  // Grant Tier Dialog state
  const [grantTierDialogOpen, setGrantTierDialogOpen] = useState(false);
  const [selectedOrgForTier, setSelectedOrgForTier] = useState<any>(null);
  const [grantTierData, setGrantTierData] = useState<{
    tier: 'FAMILY';
    duration: 'forever' | '1month' | '3months' | '6months' | '1year';
    reason: string;
  }>({ tier: 'FAMILY', duration: '1month', reason: '' });
  const [grantTierLoading, setGrantTierLoading] = useState(false);
  const [grantTierError, setGrantTierError] = useState<string | null>(null);

  const formatTier = (tier: string) => {
    switch (tier) {
      case 'STARTER': return 'Starter';
      case 'FAMILY': return 'Family';
      case 'TEAM': return 'Team';
      default: return tier;
    }
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  const loadOrganizations = async () => {
    try {
      setOrgsLoading(true);
      const orgs = await superAdminService.getAllOrganizations();
      setOrganizations(orgs.map(org => ({
        ...org,
        memberCount: org._count.memberships,
        playerCount: org._count.players,
        leagueCount: org._count.leagues,
        createdAt: new Date(org.createdAt),
      })));
      setOrgsLoaded(true);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setOrgsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const [allUsers, allInterests, stats, allInvitations] = await Promise.all([
        superAdminService.getAllUsers(),
        superAdminService.getAppInterests(interestStatusFilter || undefined),
        superAdminService.getAppInterestStats(),
        superAdminService.getPendingInvitations(),
      ]);
      setUsers(allUsers.map(user => ({
        ...user,
        name: user.email.split('@')[0],
        isLocked: (user.failedLoginAttempts ?? 0) >= 5,
        failedLoginAttempts: user.failedLoginAttempts ?? 0,
        organizations: (user as any).memberships?.map((m: any) => m.organization.name) || [],
        createdAt: new Date(user.createdAt),
      })));
      setInterests(allInterests);
      setInterestStats(stats);
      setPendingInvitations(allInvitations);
      setUsersLoaded(true);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Always reload data when switching to a tab (shows stale data while refreshing)
  useEffect(() => {
    if (currentTab === 'organizations') {
      loadOrganizations();
    } else if (currentTab === 'users') {
      loadUsers();
    }
  }, [currentTab]);

  // Delay skeleton display to avoid flash on fast loads
  useEffect(() => {
    if (orgsLoading && !orgsLoaded) {
      const timer = setTimeout(() => setShowOrgsSkeleton(true), 150);
      return () => clearTimeout(timer);
    }
    setShowOrgsSkeleton(false);
  }, [orgsLoading, orgsLoaded]);

  useEffect(() => {
    if (usersLoading && !usersLoaded) {
      const timer = setTimeout(() => setShowUsersSkeleton(true), 150);
      return () => clearTimeout(timer);
    }
    setShowUsersSkeleton(false);
  }, [usersLoading, usersLoaded]);

  // Reload interests when filter changes
  useEffect(() => {
    if (currentTab === 'users') {
      loadUsers();
    }
  }, [interestStatusFilter]);

  const handleSuspend = (org: any) => {
    setSelectedOrg(org);
    setSuspendReason('');
    setSuspendDialogOpen(true);
  };

  const confirmSuspend = async () => {
    if (!selectedOrg) return;
    try {
      await superAdminService.suspendOrganization(selectedOrg.id, suspendReason);
      toast.success(`${selectedOrg.name} has been suspended`);
      setSuspendDialogOpen(false);
      const allOrgs = await superAdminService.getAllOrganizations();
      setOrganizations(allOrgs);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to suspend organization');
    }
  };

  const handleUnsuspend = async (org: any) => {
    try {
      await superAdminService.unsuspendOrganization(org.id);
      toast.success(`${org.name} has been unsuspended`);
      const allOrgs = await superAdminService.getAllOrganizations();
      setOrganizations(allOrgs);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to unsuspend organization');
    }
  };

  const handleDelete = (org: any) => {
    setSelectedOrg(org);
    setDeleteOrgConfirmName('');
    setDeleteOrgError(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrg = async () => {
    if (!selectedOrg || deleteOrgConfirmName !== selectedOrg.name) {
      return;
    }

    try {
      setDeleteOrgError(null);
      await superAdminService.deleteOrganization(selectedOrg.id, deleteOrgConfirmName);

      // Clear states that reference the deleted org
      if (expandedOrgId === selectedOrg.id) {
        setExpandedOrgId(null);
      }
      setSelectedOrg(null);
      setDeleteDialogOpen(false);
      setDeleteOrgConfirmName('');

      // Refresh the organizations list
      const allOrgs = await superAdminService.getAllOrganizations();
      setOrganizations(allOrgs);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to delete organization';
      setDeleteOrgError(errorMessage);
    }
  };

  const handleResetPassword = (user: any) => {
    setSelectedUser(user);
    setResetReason('');
    setResetPasswordDialogOpen(true);
  };

  const handleUnlockAccount = (user: any) => {
    setSelectedUser(user);
    setUnlockAccountDialogOpen(true);
  };

  const handleViewLoginHistory = async (user: any) => {
    setSelectedUser(user);
    const isLocked = user.lockedUntil && new Date(user.lockedUntil) > new Date();
    setLoginHistory({
      email: user.email,
      failedLoginAttempts: user.failedLoginAttempts || 0,
      isLocked,
      lockedUntil: isLocked ? user.lockedUntil : null,
      hasActiveResetToken: !!user.passwordResetToken,
      accountCreated: user.createdAt,
      emailVerified: user.emailVerified,
      hasGoogle: !!user.googleId,
    });
    setLoginHistoryDialogOpen(true);
  };

  const handleDeleteUser = (selectedUser: any) => {
    if (selectedUser.id === currentUser?.id) {
      return; // Cannot delete yourself
    }
    setSelectedUser(selectedUser);
    setDeleteConfirmEmail(''); // Reset confirmation email
    setDeleteUserDialogOpen(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedUser || !resetReason.trim()) {
      return;
    }
    try {
      await superAdminService.forcePasswordReset(selectedUser.id, resetReason);
      setResetPasswordDialogOpen(false);
      setResetReason('');
      toast.success('Password reset email sent successfully. User sessions have been invalidated.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error;
      toast.error(typeof errorMsg === 'string' ? errorMsg : errorMsg?.message || 'Failed to send password reset email');
    }
  };

  const confirmUnlockAccount = async () => {
    if (!selectedUser) {
      return;
    }
    try {
      await superAdminService.unlockAccount(selectedUser.id);
      setUnlockAccountDialogOpen(false);
      toast.success('Account unlocked successfully');
      // Refresh users list
      const allUsers = await superAdminService.getAllUsers();
      setUsers(allUsers);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error;
      toast.error(typeof errorMsg === 'string' ? errorMsg : errorMsg?.message || 'Failed to unlock account');
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser || deleteConfirmEmail !== selectedUser.email) {
      return; // Email confirmation doesn't match
    }

    try {
      await userService.deleteUser(selectedUser.id);

      // Refresh the users list by reloading user data
      const allUsers = await superAdminService.getAllUsers();
      setUsers(allUsers.map(user => ({
        ...user,
        name: user.email.split('@')[0],
        isLocked: (user.failedLoginAttempts ?? 0) >= 5,
        createdAt: new Date(user.createdAt),
      })));

      setDeleteUserDialogOpen(false);
      setDeleteConfirmEmail('');
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleInvalidateUserTokens = (user: any) => {
    if (user.id === currentUser?.id) {
      return; // Cannot invalidate your own tokens
    }
    setSelectedUser(user);
    setInvalidateTokensDialogOpen(true);
  };

  const confirmInvalidateUserTokens = async () => {
    if (!selectedUser) return;

    try {
      setInvalidateTokensLoading(true);
      const result = await superAdminService.invalidateUserTokens(selectedUser.id);
      console.log(`Invalidated ${result.revokedCount} tokens for user ${selectedUser.email}`);
      setInvalidateTokensDialogOpen(false);
    } catch (error) {
      console.error('Failed to invalidate user tokens:', error);
    } finally {
      setInvalidateTokensLoading(false);
    }
  };

  const confirmInvalidateAllTokens = async () => {
    try {
      setInvalidateTokensLoading(true);
      const result = await superAdminService.invalidateAllTokens();
      console.log(`Invalidated ${result.revokedCount} tokens for all users`);
      setInvalidateAllTokensDialogOpen(false);
    } catch (error) {
      console.error('Failed to invalidate all tokens:', error);
    } finally {
      setInvalidateTokensLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrg.name.trim()) {
      setCreateOrgError('Organization name is required');
      return;
    }

    try {
      setCreateOrgLoading(true);
      setCreateOrgError(null);

      await superAdminService.createOrganization({
        name: newOrg.name,
        slug: newOrg.slug || undefined,
        ownerEmail: newOrg.ownerEmail || undefined,
        ownerRole: newOrg.ownerEmail ? newOrg.ownerRole : undefined,
      });

      // Refresh organizations list
      const orgs = await superAdminService.getAllOrganizations();
      setOrganizations(orgs.map(org => ({
        ...org,
        memberCount: org._count.memberships,
        playerCount: org._count.players,
        leagueCount: org._count.leagues,
        createdAt: new Date(org.createdAt),
      })));

      // Reset and close dialog
      setCreateOrgDialogOpen(false);
      setNewOrg({ name: '', slug: '', ownerEmail: '', ownerRole: 'OWNER' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to create organization';
      setCreateOrgError(errorMessage);
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleOpenInviteDialog = (org: any) => {
    setSelectedOrgForInvite(org);
    setInviteData({ email: '', role: 'MEMBER' });
    setInviteError(null);
    setInviteDialogOpen(true);
  };

  const handleToggleUnlimitedQuota = async (org: any) => {
    try {
      const result = await superAdminService.toggleUnlimitedQuota(org.id);
      toast.success(result.message);
      const orgs = await superAdminService.getAllOrganizations();
      setOrganizations(orgs.map((o: any) => ({
        ...o,
        memberCount: o._count.memberships,
        playerCount: o._count.players,
        leagueCount: o._count.leagues,
        createdAt: new Date(o.createdAt),
      })));
    } catch {
      toast.error('Failed to toggle unlimited quota');
    }
  };

  const handleOpenGrantTierDialog = (org: any) => {
    setSelectedOrgForTier(org);
    setGrantTierData({ tier: 'FAMILY', duration: '1month', reason: '' });
    setGrantTierError(null);
    setGrantTierDialogOpen(true);
  };

  const handleGrantTier = async () => {
    if (!selectedOrgForTier || !grantTierData.reason.trim()) {
      setGrantTierError('Reason is required');
      return;
    }

    try {
      setGrantTierLoading(true);
      setGrantTierError(null);

      await superAdminService.grantTier(selectedOrgForTier.id, grantTierData);

      // Refresh organizations list
      const orgs = await superAdminService.getAllOrganizations();
      setOrganizations(orgs.map(org => ({
        ...org,
        memberCount: org._count.memberships,
        playerCount: org._count.players,
        leagueCount: org._count.leagues,
        createdAt: new Date(org.createdAt),
      })));

      setGrantTierDialogOpen(false);
      setSelectedOrgForTier(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to grant tier';
      setGrantTierError(errorMessage);
    } finally {
      setGrantTierLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!selectedOrgForInvite || !inviteData.email.trim()) {
      setInviteError('Email is required');
      return;
    }

    try {
      setInviteLoading(true);
      setInviteError(null);

      await superAdminService.inviteToOrganization(selectedOrgForInvite.id, {
        email: inviteData.email,
        role: inviteData.role,
      });

      // Close dialog and refresh invitations list
      setInviteDialogOpen(false);
      setSelectedOrgForInvite(null);
      setInviteData({ email: '', role: 'MEMBER' });
      loadUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to send invitation';
      setInviteError(errorMessage);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!selectedInvitation) return;
    try {
      setCancelInvitationLoading(true);
      await superAdminService.cancelInvitation(selectedInvitation.id);
      setCancelInvitationDialogOpen(false);
      setSelectedInvitation(null);
      loadUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to cancel invitation');
    } finally {
      setCancelInvitationLoading(false);
    }
  };

  const handleOpenWaitlistInvite = (interest: AppInterest) => {
    setSelectedWaitlistUser(interest);
    setWaitlistOrgName('');
    setWaitlistInviteError(null);
    setWaitlistInviteDialogOpen(true);
  };

  const handleWaitlistInvite = async () => {
    if (!selectedWaitlistUser || !waitlistOrgName.trim()) {
      setWaitlistInviteError('Organization name is required');
      return;
    }

    try {
      setWaitlistInviteLoading(true);
      setWaitlistInviteError(null);

      // Create organization with the waitlist user as owner
      await superAdminService.createOrganization({
        name: waitlistOrgName,
        ownerEmail: selectedWaitlistUser.email,
        ownerRole: 'OWNER',
      });

      // Update the interest status to invited
      await superAdminService.updateAppInterestStatus(selectedWaitlistUser.id, { status: 'invited' });

      // Close dialog and refresh
      setWaitlistInviteDialogOpen(false);
      setSelectedWaitlistUser(null);
      setWaitlistOrgName('');
      loadUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to create organization';
      setWaitlistInviteError(errorMessage);
    } finally {
      setWaitlistInviteLoading(false);
    }
  };


  return (
    <Box sx={{ display: 'flex', minHeight: IS_ADMIN_PORTAL ? '100%' : '100vh', mt: IS_ADMIN_PORTAL ? 0 : -8, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Vertical Sidebar Menu - Desktop only */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        width: 240,
        flexShrink: 0,
        pr: 3,
        pl: 3,
        pt: IS_ADMIN_PORTAL ? 3 : 10
      }}>
        <Box component="nav">
          <Typography variant="h6" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            Operations
          </Typography>
          {([
            { tab: 'organizations', label: 'Organizations', icon: <BusinessIcon /> },
            { tab: 'users', label: 'Users', icon: <PeopleIcon /> },
            { tab: 'catalog', label: 'League Catalog', icon: <CategoryIcon /> },
          ] as const).map(({ tab, label, icon }) => (
            <Button
              key={tab}
              fullWidth
              variant="text"
              sx={{
                justifyContent: 'flex-start',
                pl: 2,
                mb: 0.5,
                ...(currentTab === tab && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }),
              }}
              onClick={() => changeTab(tab)}
              startIcon={icon}
            >
              {label}
            </Button>
          ))}
          <Typography variant="h6" sx={{ mt: 2, mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            System
          </Typography>
          {([
            { tab: 'insights', label: 'Insights', icon: <InsightsIcon /> },
            { tab: 'logs', label: 'Logs', icon: <ArticleIcon /> },
            { tab: 'settings', label: 'Settings', icon: <SettingsIcon /> },
          ] as const).map(({ tab, label, icon }) => (
            <Button
              key={tab}
              fullWidth
              variant="text"
              sx={{
                justifyContent: 'flex-start',
                pl: 2,
                mb: 0.5,
                ...(currentTab === tab && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }),
              }}
              onClick={() => changeTab(tab)}
              startIcon={icon}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Main Content */}
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        maxWidth: { xs: '100%', md: 800 },
        pl: { xs: 2, md: 3 },
        pr: { xs: 2, md: 3 },
        pt: { xs: 2, md: IS_ADMIN_PORTAL ? 3 : 10 },
        pb: { xs: 10, md: 3 }
      }}>
        {/* Spacer for mobile app bar (regular app only — AdminLayout has its own) */}
        {!IS_ADMIN_PORTAL && (
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Toolbar />
          </Box>
        )}

      {/* Organizations Tab */}
      {currentTab === 'organizations' && (
        !orgsLoaded && orgsLoading ? (
          showOrgsSkeleton ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Skeleton variant="text" width={180} height={40} />
                <Skeleton variant="rounded" width={80} height={36} />
              </Box>
              <Skeleton variant="text" width={300} sx={{ mb: 3 }} />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : null
        ) : <>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Organizations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage all organizations on the platform
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewOrg({ name: '', slug: '', ownerEmail: '', ownerRole: 'OWNER' });
                setCreateOrgError(null);
                setCreateOrgDialogOpen(true);
              }}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Add
            </Button>
          </Box>

          {/* Desktop Table View */}
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, overflowX: "auto", width: "100%" }}>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell width="25%">Organization</TableCell>
                  <TableCell width="15%">Subscription</TableCell>
                  <TableCell width="15%">Status</TableCell>
                  <TableCell width="15%" align="right">Members</TableCell>
                  <TableCell width="15%" align="right">Players</TableCell>
                  <TableCell width="15%" align="right">Leagues</TableCell>
                  <TableCell width={40}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <React.Fragment key={org.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedOrgId(expandedOrgId === org.id ? null : org.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {org.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {org.slug}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatTier(org.effectiveTier || org.subscriptionTier)}
                          size="small"
                          color={org.manualTierOverride ? 'secondary' : 'primary'}
                          title={org.manualTierOverride ? `Manual override (base: ${formatTier(org.subscriptionTier)})` : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(org.subscriptionStatus)}
                          size="small"
                          color={org.subscriptionStatus === 'ACTIVE' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">{org.memberCount}</TableCell>
                      <TableCell align="right">{org.playerCount}</TableCell>
                      <TableCell align="right">{org.leagueCount}</TableCell>
                      <TableCell sx={{ py: 0, px: 1 }}>
                        <KeyboardArrowDownIcon
                          sx={{
                            transform: expandedOrgId === org.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            color: 'action.active',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    {expandedOrgId === org.id && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 2 }}>
                          <Box display="flex" gap={1} sx={{ '& .MuiButton-root': { minWidth: 115 } }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInviteDialog(org);
                              }}
                            >
                              Invite
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              startIcon={<GiftIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenGrantTierDialog(org);
                              }}
                            >
                              Upgrade
                            </Button>
                            <Button
                              variant={org.unlimitedQuota ? 'contained' : 'outlined'}
                              size="small"
                              color="info"
                              startIcon={<AllInclusiveIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleUnlimitedQuota(org);
                              }}
                            >
                              {org.unlimitedQuota ? 'Unlimited' : 'Unlimited'}
                            </Button>
                            <Box sx={{ flexGrow: 1 }} />
                            {org.subscriptionStatus === 'SUSPENDED' ? (
                              <Button
                                variant="outlined"
                                size="small"
                                color="success"
                                startIcon={<LockOpenIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnsuspend(org);
                                }}
                              >
                                Unsuspend
                              </Button>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<BlockIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSuspend(org);
                                }}
                              >
                                Suspend
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(org);
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {organizations.map((org) => (
              <Card key={org.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight="medium">
                        {org.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.slug}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatStatus(org.subscriptionStatus)}
                      size="small"
                      color={org.subscriptionStatus === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Subscription
                      </Typography>
                      <Box>
                        <Chip
                          label={formatTier(org.effectiveTier || org.subscriptionTier)}
                          size="small"
                          color={org.manualTierOverride ? 'secondary' : 'primary'}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Members
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {org.memberCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Players
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {org.playerCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        Leagues
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {org.leagueCount}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box display="flex" flexWrap="wrap" gap={1} justifyContent="space-between" sx={{ '& .MuiButton-root': { minWidth: 115 } }}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => handleOpenInviteDialog(org)}
                      >
                        Invite
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={<GiftIcon />}
                        onClick={() => handleOpenGrantTierDialog(org)}
                      >
                        Upgrade
                      </Button>
                      <Button
                        variant={org.unlimitedQuota ? 'contained' : 'outlined'}
                        size="small"
                        color="info"
                        startIcon={<AllInclusiveIcon />}
                        onClick={() => handleToggleUnlimitedQuota(org)}
                      >
                        Unlimited
                      </Button>
                    </Box>
                    <Box display="flex" gap={1}>
                      {org.subscriptionStatus === 'SUSPENDED' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          startIcon={<LockOpenIcon />}
                          onClick={() => handleUnsuspend(org)}
                        >
                          Unsuspend
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<BlockIcon />}
                          onClick={() => handleSuspend(org)}
                        >
                          Suspend
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(org)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
          {/* Floating Action Button - Mobile */}
          {isMobile && (
            <Fab
              color="primary"
              ref={fabRef}
              aria-label="add organization"
              onClick={() => {
                setNewOrg({ name: '', slug: '', ownerEmail: '', ownerRole: 'OWNER' });
                setCreateOrgError(null);
                setCreateOrgDialogOpen(true);
              }}
              sx={{
                position: 'fixed',
                bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
                right: 16,
                zIndex: 1000,
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <AddIcon />
            </Fab>
          )}
          </>
        )}

        {/* User Management Tab */}
        {currentTab === 'users' && (
          !usersLoaded && usersLoading ? (
            showUsersSkeleton ? (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Skeleton variant="text" width={120} height={40} />
                </Box>
                <Skeleton variant="text" width={280} sx={{ mb: 3 }} />
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : null
          ) : <>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  User Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage user accounts, credentials, and access control
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => setInvalidateAllTokensDialogOpen(true)}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                Logout All Users
              </Button>
            </Box>

            {/* Mobile: Logout All Users */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setInvalidateAllTokensDialogOpen(true)}
              fullWidth
              sx={{ mb: 2, display: { xs: 'flex', md: 'none' } }}
            >
              Logout All Users
            </Button>

            {/* Waitlist Accordion */}
            <Accordion defaultExpanded={false} disableGutters sx={{ mb: 2, mt: -1, pt: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={500}>Waitlist</Typography>
                  {interestStats && interestStats.pending > 0 && (
                    <Chip label={interestStats.pending} size="small" color="warning" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Stats Cards */}
                {interestStats && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="h5" color="primary">{interestStats.total}</Typography>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="h5" color="warning.main">{interestStats.pending}</Typography>
                          <Typography variant="caption" color="text.secondary">Pending</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="h5" color="info.main">{interestStats.invited}</Typography>
                          <Typography variant="caption" color="text.secondary">Invited</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="h5" color="success.main">{interestStats.converted}</Typography>
                          <Typography variant="caption" color="text.secondary">Converted</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Filter */}
                <Box sx={{ mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={interestStatusFilter}
                      label="Filter by Status"
                      onChange={(e) => setInterestStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="invited">Invited</MenuItem>
                      <MenuItem value="converted">Converted</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Interests Table — Desktop */}
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Signed Up</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No waitlist entries found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        interests.map((interest) => (
                          <TableRow key={interest.id}>
                            <TableCell>{interest.email}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatStatus(interest.status)}
                                color={
                                  interest.status === 'converted' ? 'success' :
                                  interest.status === 'invited' ? 'info' : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(interest.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {interest.status === 'pending' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SendIcon />}
                                  onClick={() => handleOpenWaitlistInvite(interest)}
                                >
                                  Invite
                                </Button>
                              )}
                              {interest.status === 'invited' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<CheckIcon />}
                                  onClick={async () => {
                                    await superAdminService.updateAppInterestStatus(interest.id, { status: 'converted' });
                                    loadUsers();
                                  }}
                                >
                                  Mark Converted
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={async () => {
                                  if (window.confirm('Delete this waitlist record?')) {
                                    await superAdminService.deleteAppInterest(interest.id);
                                    loadUsers();
                                  }
                                }}
                                sx={{ ml: 1 }}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Interests Table — Mobile (expandable rows) */}
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell width={40}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No waitlist entries found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        interests.map((interest) => (
                          <React.Fragment key={interest.id}>
                            <TableRow
                              onClick={() => setExpandedUserId(expandedUserId === `wl-${interest.id}` ? null : `wl-${interest.id}`)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {interest.email}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={formatStatus(interest.status)}
                                  color={
                                    interest.status === 'converted' ? 'success' :
                                    interest.status === 'invited' ? 'info' : 'warning'
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0, px: 1 }}>
                                <KeyboardArrowDownIcon
                                  sx={{
                                    transform: expandedUserId === `wl-${interest.id}` ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                            {expandedUserId === `wl-${interest.id}` && (
                              <TableRow>
                                <TableCell colSpan={3} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Signed up: {new Date(interest.createdAt).toLocaleDateString()}
                                  </Typography>
                                  <Box display="flex" gap={1} mt={1}>
                                    {interest.status === 'pending' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<SendIcon />}
                                        onClick={() => handleOpenWaitlistInvite(interest)}
                                      >
                                        Invite
                                      </Button>
                                    )}
                                    {interest.status === 'invited' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<CheckIcon />}
                                        onClick={async () => {
                                          await superAdminService.updateAppInterestStatus(interest.id, { status: 'converted' });
                                          loadUsers();
                                        }}
                                      >
                                        Mark Converted
                                      </Button>
                                    )}
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      startIcon={<DeleteIcon />}
                                      onClick={async () => {
                                        if (window.confirm('Delete this waitlist record?')) {
                                          await superAdminService.deleteAppInterest(interest.id);
                                          loadUsers();
                                        }
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {/* Pending Invitations Accordion */}
            <Accordion defaultExpanded={pendingInvitations.length > 0} disableGutters sx={{ mb: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={500}>Pending Invitations</Typography>
                  {pendingInvitations.length > 0 && (
                    <Chip label={pendingInvitations.length} size="small" color="info" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Desktop */}
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingInvitations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No pending invitations
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        pendingInvitations.map((inv) => (
                          <TableRow key={inv.id}>
                            <TableCell>{inv.email}</TableCell>
                            <TableCell>{inv.organization.name}</TableCell>
                            <TableCell>
                              <Chip label={inv.role} size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color={new Date(inv.expiresAt) < new Date() ? 'error' : 'text.secondary'}
                              >
                                {new Date(inv.expiresAt).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={() => {
                                  setSelectedInvitation(inv);
                                  setCancelInvitationDialogOpen(true);
                                }}
                              >
                                Cancel
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Mobile */}
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Org</TableCell>
                        <TableCell width={40}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingInvitations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No pending invitations
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        pendingInvitations.map((inv) => (
                          <React.Fragment key={inv.id}>
                            <TableRow
                              onClick={() => setExpandedInvitationId(expandedInvitationId === inv.id ? null : inv.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {inv.email}
                              </TableCell>
                              <TableCell>{inv.organization.name}</TableCell>
                              <TableCell sx={{ py: 0, px: 1 }}>
                                <KeyboardArrowDownIcon
                                  sx={{
                                    transform: expandedInvitationId === inv.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                            {expandedInvitationId === inv.id && (
                              <TableRow>
                                <TableCell colSpan={3} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Role: {inv.role} &middot; Expires: {new Date(inv.expiresAt).toLocaleDateString()}
                                  </Typography>
                                  <Box display="flex" gap={1} mt={1}>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      startIcon={<CancelIcon />}
                                      onClick={async () => {
                                        if (window.confirm(`Cancel invitation to ${inv.email}?`)) {
                                          try {
                                            await superAdminService.cancelInvitation(inv.id);
                                            loadUsers();
                                          } catch (err: any) {
                                            toast.error(err.response?.data?.error?.message || 'Failed to cancel invitation');
                                          }
                                        }
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {/* Desktop Table View */}
            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, overflowX: "auto", width: "100%" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Organizations</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Failed Attempts</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell width={40}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <React.Fragment key={user.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.organizations?.join(', ') || 'None'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isLocked ? 'Locked' : 'Active'}
                            size="small"
                            color={user.isLocked ? 'error' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={user.failedLoginAttempts}
                            size="small"
                            color={user.failedLoginAttempts >= 3 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ py: 0, px: 1 }}>
                          <KeyboardArrowDownIcon
                            sx={{
                              transform: expandedUserId === user.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      {expandedUserId === user.id && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 2 }}>
                            <Box display="flex" gap={2}>
                              <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                startIcon={<LockResetIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetPassword(user);
                                }}
                              >
                                Password Reset
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="success"
                                startIcon={<LockOpenIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlockAccount(user);
                                }}
                                disabled={!user.isLocked}
                              >
                                Unlock
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<HistoryIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewLoginHistory(user);
                                }}
                              >
                                Login History
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<BlockIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInvalidateUserTokens(user);
                                }}
                                disabled={user.id === currentUser?.id}
                              >
                                Logout
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteUser(user);
                                }}
                                disabled={user.id === currentUser?.id}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {users.map((user) => (
                <Card key={user.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight="medium">
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                      <Chip
                        label={user.isLocked ? 'Locked' : 'Active'}
                        size="small"
                        color={user.isLocked ? 'error' : 'success'}
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Organizations
                        </Typography>
                        <Typography variant="body2">
                          {user.organizations?.join(', ') || 'None'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Failed Attempts
                        </Typography>
                        <Box>
                          <Chip
                            label={user.failedLoginAttempts}
                            size="small"
                            color={user.failedLoginAttempts >= 3 ? 'warning' : 'default'}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={<LockResetIcon />}
                        onClick={() => handleResetPassword(user)}
                        fullWidth
                      >
                        Password Reset
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={<LockOpenIcon />}
                        onClick={() => handleUnlockAccount(user)}
                        fullWidth
                        disabled={!user.isLocked}
                      >
                        Unlock
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon />}
                        onClick={() => handleViewLoginHistory(user)}
                        fullWidth
                      >
                        Login History
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<BlockIcon />}
                        onClick={() => handleInvalidateUserTokens(user)}
                        disabled={user.id === currentUser?.id}
                        fullWidth
                      >
                        Logout
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteUser(user)}
                        disabled={user.id === currentUser?.id}
                        fullWidth
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}

        {/* League Catalog Tab */}
        {currentTab === 'catalog' && (
          <GlobalLeagueCatalogTab />
        )}

        {/* Insights Tab (combined Analytics + Monitoring) */}
        {currentTab === 'insights' && (
          <InsightsTab />
        )}

        {/* Logs Tab */}
        {currentTab === 'logs' && (
          <LogsTab />
        )}

        {/* Settings Tab */}
        {currentTab === 'settings' && (
          <SettingsPanel />
        )}

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BlockIcon />Suspend Organization</Box></DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to suspend <strong>{selectedOrg?.name}</strong>? Users will not be able to access
            this organization.
          </Typography>
          <TextField
            fullWidth
            label="Reason for suspension (optional)"
            multiline
            rows={3}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSuspendDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button variant="contained" color="warning" startIcon={<BlockIcon />} onClick={confirmSuspend}>
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon />Delete Organization</Box></DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          {deleteOrgError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {deleteOrgError}
            </Alert>
          )}
          <Typography>
            Are you sure you want to permanently delete <strong>{selectedOrg?.name}</strong>? All data will be lost.
          </Typography>
          <TextField
            fullWidth
            label="Type organization name to confirm"
            value={deleteOrgConfirmName}
            onChange={(e) => setDeleteOrgConfirmName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteOrg}
            disabled={deleteOrgConfirmName !== selectedOrg?.name}
            startIcon={<DeleteIcon />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LockResetIcon />Force Password Reset</Box></DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will invalidate the user's current sessions and send them a password reset email.
          </Alert>
          <Typography gutterBottom>
            User: <strong>{selectedUser?.name}</strong> ({selectedUser?.email})
          </Typography>
          <TextField
            fullWidth
            label="Reason for password reset (required)"
            multiline
            rows={3}
            value={resetReason}
            onChange={(e) => setResetReason(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setResetPasswordDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmResetPassword}
            disabled={!resetReason.trim()}
            startIcon={<LockResetIcon />}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Account Dialog */}
      <Dialog open={unlockAccountDialogOpen} onClose={() => setUnlockAccountDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LockOpenIcon />Unlock User Account</Box></DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to unlock the account for <strong>{selectedUser?.name}</strong> (
            {selectedUser?.email})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will reset the failed login attempts counter and allow the user to log in
            immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setUnlockAccountDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button variant="contained" color="success" onClick={confirmUnlockAccount} startIcon={<LockOpenIcon />}>
            Unlock Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login History Dialog */}
      <Dialog
        open={loginHistoryDialogOpen}
        onClose={() => setLoginHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><HistoryIcon />User Login History</Box></DialogTitle>
        <DialogContent>
          {loginHistory && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                User: {selectedUser?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email: {loginHistory.email}
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Account Status
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        label={loginHistory.isLocked ? 'Locked' : 'Active'}
                        size="small"
                        color={loginHistory.isLocked ? 'error' : 'success'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Failed Login Attempts
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        label={loginHistory.failedLoginAttempts}
                        size="small"
                        color={loginHistory.failedLoginAttempts >= 3 ? 'warning' : 'default'}
                      />
                    </Typography>
                  </Grid>
                  {loginHistory.isLocked && loginHistory.lockedUntil && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Locked Until
                      </Typography>
                      <Typography variant="body2">
                        {new Date(loginHistory.lockedUntil).toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email Verified
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        label={loginHistory.emailVerified ? 'Yes' : 'No'}
                        size="small"
                        color={loginHistory.emailVerified ? 'success' : 'default'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Active Reset Token
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        label={loginHistory.hasActiveResetToken ? 'Yes' : 'No'}
                        size="small"
                        color={loginHistory.hasActiveResetToken ? 'warning' : 'default'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Account Created
                    </Typography>
                    <Typography variant="body2">
                      {new Date(loginHistory.accountCreated).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Linked Accounts
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      {loginHistory.hasGoogle && <Chip label="Google" size="small" color="primary" variant="outlined" />}
                      {!loginHistory.hasGoogle && <Chip label="None" size="small" />}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setLoginHistoryDialogOpen(false)} startIcon={<CancelIcon />}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Invitation Dialog */}
      <Dialog open={cancelInvitationDialogOpen} onClose={() => setCancelInvitationDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CancelIcon />Cancel Invitation</Box></DialogTitle>
        <DialogContent>
          <Typography>
            Cancel the invitation to <strong>{selectedInvitation?.email}</strong> for organization <strong>{selectedInvitation?.organization.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelInvitationDialogOpen(false)}>Back</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelInvitation}
            disabled={cancelInvitationLoading}
            startIcon={<CancelIcon />}
          >
            Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialogOpen} onClose={() => setDeleteUserDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon />Delete User</Box></DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})? All user data will be lost.
          </Typography>
          <TextField
            fullWidth
            label="Type user email to confirm"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteUserDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteUser}
            disabled={deleteConfirmEmail !== selectedUser?.email}
            startIcon={<DeleteIcon />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgDialogOpen} onClose={() => setCreateOrgDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AddIcon />Create Organization</Box></DialogTitle>
        <DialogContent>
          {createOrgError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createOrgError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Organization Name"
            value={newOrg.name}
            onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Slug (optional)"
            value={newOrg.slug}
            onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
            sx={{ mt: 2 }}
            helperText="Auto-generated from name if not provided. Only lowercase letters, numbers, and hyphens."
          />
          <Divider sx={{ my: 3 }} />
          <Alert severity="info" sx={{ mb: 2 }}>
            Optionally invite an owner. They will receive an email invitation to join the organization.
          </Alert>
          <TextField
            fullWidth
            label="Owner Email (optional)"
            type="email"
            value={newOrg.ownerEmail}
            onChange={(e) => setNewOrg({ ...newOrg, ownerEmail: e.target.value })}
            sx={{ mt: 2 }}
          />
          {newOrg.ownerEmail && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Owner Role</InputLabel>
              <Select
                value={newOrg.ownerRole}
                label="Owner Role"
                onChange={(e) => setNewOrg({ ...newOrg, ownerRole: e.target.value as 'OWNER' | 'ADMIN' })}
              >
                <MenuItem value="OWNER">Owner</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
              <FormHelperText>Role the invited user will have in the organization</FormHelperText>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCreateOrgDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateOrganization}
            disabled={!newOrg.name.trim() || createOrgLoading}
            startIcon={<AddIcon />}
          >
            {createOrgLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User to Organization Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonAddIcon />Invite User to {selectedOrgForInvite?.name}</Box></DialogTitle>
        <DialogContent>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            The user will receive an email invitation to join this organization.
          </Alert>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={inviteData.email}
            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteData.role}
              label="Role"
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as 'OWNER' | 'ADMIN' | 'MEMBER' })}
            >
              <MenuItem value="OWNER">Owner</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MEMBER">Member</MenuItem>
            </Select>
            <FormHelperText>Role the user will have in the organization</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setInviteDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInviteUser}
            disabled={!inviteData.email.trim() || inviteLoading}
            startIcon={<SendIcon />}
          >
            {inviteLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waitlist Invite Dialog */}
      <Dialog open={waitlistInviteDialogOpen} onClose={() => setWaitlistInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MailIcon />Invite to Player Central</Box></DialogTitle>
        <DialogContent>
          {waitlistInviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {waitlistInviteError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Create an organization for {selectedWaitlistUser?.email}. They will receive an email invitation to join as the owner.
          </Alert>
          <TextField
            fullWidth
            label="Organization Name"
            value={waitlistOrgName}
            onChange={(e) => setWaitlistOrgName(e.target.value)}
            placeholder="e.g., Smith Family Soccer"
            helperText="The name for the user's new organization"
            sx={{ mt: 2 }}
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setWaitlistInviteDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleWaitlistInvite}
            disabled={!waitlistOrgName.trim() || waitlistInviteLoading}
            startIcon={<SendIcon />}
          >
            {waitlistInviteLoading ? 'Creating...' : 'Create & Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout User Dialog */}
      <Dialog open={invalidateTokensDialogOpen} onClose={() => setInvalidateTokensDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LogoutIcon />Logout User</Box></DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately log out the user from all devices.
          </Alert>
          <Typography>
            Are you sure you want to log out <strong>{selectedUser?.name}</strong> ({selectedUser?.email}) from all devices?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The user will need to log in again on all their devices.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setInvalidateTokensDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmInvalidateUserTokens}
            disabled={invalidateTokensLoading}
            startIcon={<LogoutIcon />}
          >
            {invalidateTokensLoading ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout All Users Dialog */}
      <Dialog open={invalidateAllTokensDialogOpen} onClose={() => setInvalidateAllTokensDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LogoutIcon />Logout All Users</Box></DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will immediately log out ALL users from all devices (except you).
          </Alert>
          <Typography>
            Are you sure you want to log out all users? This is an emergency action.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All users will need to log in again. Your session will not be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setInvalidateAllTokensDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmInvalidateAllTokens}
            disabled={invalidateTokensLoading}
            startIcon={<LogoutIcon />}
          >
            {invalidateTokensLoading ? 'Logging out...' : 'Logout All Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grant Tier Dialog */}
      <Dialog open={grantTierDialogOpen} onClose={() => setGrantTierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GiftIcon />Grant Free Tier Upgrade</Box></DialogTitle>
        <DialogContent>
          {grantTierError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {grantTierError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Grant a complimentary tier upgrade to <strong>{selectedOrgForTier?.name}</strong>. The organization will get access to all features of the selected tier without payment.
          </Alert>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tier</InputLabel>
            <Select
              value={grantTierData.tier}
              label="Tier"
              onChange={(e) => setGrantTierData({ ...grantTierData, tier: e.target.value as 'FAMILY' })}
            >
              <MenuItem value="FAMILY">Family ($4.99/mo value)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Duration</InputLabel>
            <Select
              value={grantTierData.duration}
              label="Duration"
              onChange={(e) => setGrantTierData({ ...grantTierData, duration: e.target.value as any })}
            >
              <MenuItem value="1month">1 Month</MenuItem>
              <MenuItem value="3months">3 Months</MenuItem>
              <MenuItem value="6months">6 Months</MenuItem>
              <MenuItem value="1year">1 Year</MenuItem>
              <MenuItem value="forever">Forever (No Expiration)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reason (required)"
            value={grantTierData.reason}
            onChange={(e) => setGrantTierData({ ...grantTierData, reason: e.target.value })}
            sx={{ mt: 2 }}
            multiline
            rows={3}
            placeholder="e.g., Beta tester, community leader, partner organization..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setGrantTierDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGrantTier}
            disabled={!grantTierData.reason.trim() || grantTierLoading}
            startIcon={<GiftIcon />}
          >
            {grantTierLoading ? 'Upgrading...' : 'Upgrade'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      {/* Bottom Navigation - Mobile only */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', md: 'none' },
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        elevation={3}
      >
        <BottomNavigation
          value={currentTab}
          onChange={(_, newValue) => {
            changeTab(newValue);
          }}
          showLabels
          sx={{ bgcolor: 'background.paper' }}
        >
          <BottomNavigationAction label="Orgs" value="organizations" icon={<BusinessIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
          <BottomNavigationAction label="Users" value="users" icon={<PeopleIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
          <BottomNavigationAction label="Leagues" value="catalog" icon={<CategoryIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
          <BottomNavigationAction label="Insights" value="insights" icon={<InsightsIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
          <BottomNavigationAction label="Logs" value="logs" icon={<ArticleIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
          <BottomNavigationAction label="Settings" value="settings" icon={<SettingsIcon />} sx={{ minWidth: 'auto', '& .MuiBottomNavigationAction-label': { fontSize: '0.65rem' } }} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default SuperAdminPage;
